import React from 'react';

/* ══════════════════════════════════════════════════════════════════════════
 * A BLANK SCREEN IS NOT AN ANSWER
 *
 * Al, 23 September 2026: *"right now, the app doesn't open up on my phone.
 * Uh, it's nothing's happening."*
 *
 * ⚠️ I COULD NOT REPRODUCE IT — every screen renders on the deployed build —
 * and this is not a guess at his fault. It is the reason his fault was
 * INVISIBLE, and that part I can fix.
 *
 * React unmounts the ENTIRE tree when any component throws during render. One
 * bad value anywhere — a venue row shaped differently, a stored preference
 * from an older build, a phone that refuses localStorage — and `#root` is
 * emptied. The app does not show an error. It shows nothing at all, which is
 * exactly the words he used, and it looks identical to a server being down, a
 * bad connection, or a phone that has not loaded the page.
 *
 * ⚠️ AND IT TELLS NOBODY. No message on screen, and nothing Al can send me.
 * Every fault of this shape costs a round trip of "what does it look like?"
 * — which is the round trip we just spent.
 *
 * So: the tree is wrapped. A crash now shows what broke and offers a way out,
 * and the way out is the one that actually works — clear what this browser has
 * stored for Eleat and start again, because stored state from an older build
 * is the likeliest thing to poison a render.
 *
 * ⚠️ IT DOES NOT SWALLOW THE FAULT. The error is still re-thrown to the
 * console, so the real cause is not hidden from whoever looks next. A boundary
 * that quietly absorbs errors is worse than none — it turns a blank screen
 * into a screen that lies.
 *
 * ⚠️ AND IT NEVER CLEARS ANYTHING BY ITSELF. Signing an Eleater out because a
 * screen threw would be deciding, on their behalf, that their account is the
 * problem. The button says what it will do and waits to be pressed.
 * ══════════════════════════════════════════════════════════════════════════ */

/** Which bundle is actually running, taken from the script the browser loaded. */
function buildId() {
  try {
    const s = [...document.querySelectorAll('script[src]')]
      .map((n) => n.getAttribute('src'))
      .find((v) => v && /assets\/index-/.test(v));
    const hash = s ? (s.match(/index-([\w-]+)\.js/) || [])[1] : null;
    return hash ? `build ${hash}` : 'build unknown';
  } catch { return 'build unknown'; }
}

/**
 * The throw site and its caller, trimmed to what a sourcemap needs.
 *
 * ⚠️ THE FILE:LINE:COLUMN IS THE WHOLE POINT. Minified names mean nothing, but
 * `index-QKtMYe6Q.js:41:12345` maps, through the published `.map`, to a real
 * line in a real source file. The host and the query string are stripped so it
 * fits on a phone and stays readable in a photograph.
 */
function frames(err) {
  try {
    const raw = (err && err.stack) || '';
    const lines = raw.split('\n')
      .map((l) => l.trim())
      .filter((l) => /assets\/index-/.test(l))
      .slice(0, 2)
      .map((l) => {
        const m = l.match(/(index-[\w-]+\.js):(\d+):(\d+)/);
        const at = (l.match(/^at\s+([\w$.<>]+)/) || [])[1];
        return m ? `${at ? at + ' ' : ''}${m[1]}:${m[2]}:${m[3]}` : l.slice(0, 80);
      });
    return lines.length ? lines.join('\n') : 'no stack';
  } catch { return 'no stack'; }
}

export class Boundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { err: null, served: null };
  }

  static getDerivedStateFromError(err) {
    return { err };
  }

  /* ⚠️⚠️ THE ONE QUESTION THE SCREEN COULD NOT ANSWER — 23 September 2026.
   *
   * Al's screenshot read `build QKtMYe6Q` while the server was serving
   * `index-CfJu17vd.js`. **His page was two deploys old**, and neither of us
   * could see that from the screen: it named the build that crashed and not
   * the build that was available, so "the app is broken" and "this page is
   * old" looked identical.
   *
   * Now the screen asks the server and says which it is, in words. If they
   * differ, the fault is almost certainly already fixed and the page just has
   * to be fetched again — which is a completely different instruction from
   * "send me a screenshot". */
  componentDidMount() {
    fetch('/', { cache: 'no-store' })
      .then((r) => (r.ok ? r.text() : ''))
      .then((h) => {
        const served = (h.match(/assets\/index-([\w-]+)\.js/) || [])[1] || null;
        if (served) this.setState({ served });
      })
      .catch(() => { /* offline: the screen simply says less */ });
  }

  componentDidCatch(err, info) {
    /* ⚠️ STILL SHOUTED. The boundary is for the Eleater; the console is for
     * whoever is fixing it. */
    // eslint-disable-next-line no-console
    console.error('[eleat] a screen stopped:', err, info && info.componentStack);
  }

  render() {
    if (!this.state.err) return this.props.children;

    const msg = String((this.state.err && this.state.err.message) || this.state.err);
    /* the hash this page is actually running, for the comparison below */
    const running = (buildId().match(/build (\S+)/) || [])[1] || null;

    return (
      <div className="home">
        <header className="hhead">
          <div className="htop">
            <div className="logo">
              <img className="lmark" src="/eleat_mark.png" alt="" />
              <img className="lword" src="/eleat_word.png" alt="Eleat" />
            </div>
          </div>
          <div className="hhero">
            <h1 className="heroline">Eleat stopped here</h1>
          </div>
        </header>

        <main>
          <p className="mut">
            Something on this screen went wrong, so Eleat stopped rather than
            showing you half of it. Nothing on your account has changed.
          </p>

          {/* ⚠️ THE ACTUAL MESSAGE, NOT A FRIENDLY VERSION OF IT. Al is the one
              reading this, and "something went wrong" is what we already had —
              it is a blank screen with a sentence on it. */}
          <p className="err" style={{ wordBreak: 'break-word' }}>{msg}</p>

          {/* ⚠️⚠️ AND WHICH BUILD IT WAS — 23 September 2026.
              Al photographed this screen reading "Cannot access 'a' before
              initialization". By the time I looked, the build it came from had
              already been replaced by the next deploy, and the one that was
              live rendered every screen correctly. **So I could not tell
              whether the fault was gone or hiding**, and neither could he.

              A screenshot that names the build settles that in one glance. The
              filename carries Vite's content hash, so it identifies the exact
              bundle — not the date, not the commit message, the actual bytes
              that crashed. Two deploys a minute apart are two different hashes.

              ⚠️ IT IS READ OFF THE PAGE, NOT BAKED IN. A constant written at
              build time is one more thing that can be stale; the script tag is
              what the browser actually loaded. */}
          {/* ══════════════════════════════════════════════════════════════
              ⚠️⚠️ THE LINE AND COLUMN, BECAUSE NOTHING ELSE HAS WORKED

              Al has now hit this four times. Every build I can reach renders
              every screen — signed out, signed in with the responses stubbed,
              desktop and Android, cache-busted, on the exact bundle his phone
              is running. I have run out of ways to reproduce it from here, and
              guessing again would waste another of his evenings.

              So this stops being a friendly message and starts being an
              instrument. `sourcemap: true` is set in `vite.config.js`, so the
              map for every build is published beside the bundle — which means
              a single `index-XXXX.js:41:12345` from his screen maps to the
              exact line of the exact source file. One photograph ends it.

              ⚠️ TWO FRAMES, NOT THE WHOLE STACK. The whole thing is forty
              lines of minified noise on a phone screen and he would have to
              photograph it three times. The throw site and its caller are what
              the map needs.
              ══════════════════════════════════════════════════════════════ */}
          {/* ⚠️ `pre-wrap` OR THE NEWLINES COLLAPSE and the three facts run
              together into one unreadable line in the photograph. */}
          <p className="mut" style={{ fontSize: 11, wordBreak: 'break-all',
            fontFamily: 'ui-monospace, monospace', lineHeight: 1.5,
            whiteSpace: 'pre-wrap' }}>
            {buildId()}
            {'\n'}screen {window.location.search || '(home)'}
            {'\n'}{frames(this.state.err)}
          </p>

          {/* ⚠️ SAID IN WORDS, NOT LEFT FOR SOMEBODY TO COMPARE TWO HASHES.
              Al should not have to read a build id off a screen and match it
              against one I read off a server. */}
          {this.state.served && this.state.served !== running && (
            <p className="mut" style={{ color: 'var(--action)', marginTop: 14 }}>
              <b>This page is out of date.</b> Eleat has moved on to a newer
              version since this page was opened — the fault above is very
              likely already fixed. Tap <b>Try again</b>.
            </p>
          )}
          {this.state.served && this.state.served === running && (
            <p className="mut" style={{ marginTop: 14 }}>
              This page IS the newest version, so the fault above is real.
              Please send this screen to whoever is building Eleat.
            </p>
          )}

          <p className="mut" style={{ marginTop: 18 }}>
            {/* ⚠️ A PLAIN `reload()` CAN HAND BACK THE SAME BROKEN PAGE — it
                is allowed to come from the cache, which is the likeliest thing
                wrong in the first place. This asks for the URL afresh with a
                one-off marker, so the browser has to go to the server. */}
            <button type="button" className="link" onClick={() => {
              try { sessionStorage.removeItem('eleat.reloadedFor'); } catch { /* fine */ }
              const u = new URL(window.location.href);
              u.searchParams.set('fresh', String(Date.now()));
              window.location.replace(u.toString());
            }}>Try again</button>
          </p>

          <p className="mut">
            <button type="button" className="link" onClick={() => {
              /* ⚠️ EVERYTHING ELEAT PUT THERE, AND NOTHING ELSE. Wiping the
                 whole of localStorage would take another site's data with it
                 on a shared origin. Eleat's keys are the token, the place the
                 Eleater set, the language, and the rotation counter. */
              for (const store of [window.localStorage, window.sessionStorage]) {
                try {
                  for (const k of ['eleat.token', 'eleat.centre', 'eleat.lang', 'eleat_v1']) {
                    store.removeItem(k);
                  }
                } catch { /* a browser that refuses storage has none to clear */ }
              }
              window.location.search = '';
            }}>
              Still stuck? Clear what this browser has saved and start again
            </button>
          </p>
          <p className="mut" style={{ fontSize: 11 }}>
            That signs you out on this device. Your account, your saved places
            and your reviews are on the server and are not touched.
          </p>
        </main>
      </div>
    );
  }
}
